import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  chatThemeSchema,
  resolveInterfaceTheme,
  resolveLanguage,
  DEFAULT_INTERFACE_THEME,
  type ChatTheme,
  type InterfaceThemeId,
  type LanguageCode,
} from '@kairelo/shared';
import { auth, invitations, profile as profileApi, ApiRequestError } from '../lib/api.js';
import { useOwnerSession } from '../lib/useOwnerSession.js';
import { RealtimeClient } from '../lib/realtime.js';
import {
  checkKeyStorage,
  loadConversationKey,
  storeConversationKey,
  type KeyStorageState,
} from '../lib/keystore.js';
import { buildInvitationUrl, generateConversationKey, keyToFragmentValue } from '../lib/crypto.js';
import { InterfaceThemeProvider } from '../theme/InterfaceTheme.tsx';
import { I18nProvider, useT } from '../i18n/I18n.tsx';
import { ConversationList } from '../chat/ConversationList.tsx';
import { ChatPane } from '../chat/ChatPane.tsx';
import { useConversationDirectory } from '../chat/useConversationDirectory.js';
import { G } from '../chat/icons.tsx';
import { Disclosure, Modal, Notice, useToast } from '../components/ui.tsx';
import {
  BearerLinkNotice,
  DeviceLocalKeyNotice,
  EncryptionScopeNotice,
  EndpointCopyingNotice,
  TemporaryNotice,
} from '../components/Disclosures.tsx';
import { ReportDialog } from '../chat/ReportDialog.tsx';

/** Remembers that the owner has seen a full invitation link at least once. */
const SEEN_LINK_FLAG = 'tc.seen-invitation-link';

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // A browser that refuses storage simply shows the link every time.
  }
}

/**
 * The owner's messenger.
 *
 * Two panes on a wide screen; one at a time on a phone, where opening a
 * conversation is a navigation rather than a layout change. The URL is the
 * source of truth for which conversation is open, so back works and a
 * conversation can be linked to.
 */
export function Messenger() {
  const [language, setLanguage] = useState<LanguageCode>('en');
  return (
    <I18nProvider language={language}>
      <MessengerInner language={language} onLanguage={setLanguage} />
    </I18nProvider>
  );
}

function MessengerInner({
  language,
  onLanguage,
}: {
  language: LanguageCode;
  onLanguage: (language: LanguageCode) => void;
}) {
  const t = useT();
  const { id: selectedId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const { owner, state, suspensionReason } = useOwnerSession();
  const { conversations, refresh } = useConversationDirectory();

  const [theme, setTheme] = useState<ChatTheme>(chatThemeSchema.parse({}));
  const [interfaceTheme, setInterfaceTheme] = useState<InterfaceThemeId>(DEFAULT_INTERFACE_THEME);
  const [conversationKey, setConversationKey] = useState<CryptoKey | null>(null);
  const [keyChecked, setKeyChecked] = useState(false);
  const [storage, setStorage] = useState<KeyStorageState | null>(null);
  const [creating, setCreating] = useState(false);
  const [newLink, setNewLink] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  /** First link ever: show the warnings in full; collapse them afterwards. */
  const [firstLink, setFirstLink] = useState(false);
  const [dialog, setDialog] = useState<'none' | 'about' | 'report' | 'end'>('none');
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    profileApi
      .get()
      .then((loaded) => {
        setTheme(loaded.theme);
        // Narrows to a theme this build actually has, so a preference written
        // by another build — or by the revision that named themes after other
        // messengers — resolves to the default rather than to nothing.
        setInterfaceTheme(resolveInterfaceTheme(loaded.preferredInterface));

        if (loaded.preferredLanguage) {
          onLanguage(loaded.preferredLanguage);
          return;
        }
        // Never chosen: adopt the browser's language once and record it. From
        // then on it changes only in settings, so it does not drift when
        // someone signs in from a differently configured device.
        const detected = resolveLanguage(navigator.language);
        onLanguage(detected);
        void profileApi
          .update({
            displayName: loaded.displayName,
            headline: loaded.headline,
            bio: loaded.bio,
            preferredLanguage: detected,
          })
          .catch(() => {});
      })
      .catch(() => {});
    void checkKeyStorage().then(setStorage);
  }, [onLanguage]);

  /** The key for the open conversation, from this browser profile only. */
  useEffect(() => {
    let cancelled = false;
    setKeyChecked(false);
    setConversationKey(null);
    if (!selectedId) {
      setKeyChecked(true);
      return;
    }
    void loadConversationKey(selectedId).then((key) => {
      if (cancelled) return;
      setConversationKey(key);
      setKeyChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  /** In-app notification when a visitor joins, while the app is open (§9). */
  useEffect(() => {
    if (!owner) return;
    const client = new RealtimeClient({
      scope: 'owner-inbox',
      onStatus: () => {},
      onFrame: (frame) => {
        if (frame.t === 'joined') {
          toast('success', t('messenger.someoneJoined'));
          void refresh();
        }
        if (frame.t === 'expired') void refresh();
      },
    });
    client.connect();
    return () => client.close();
  }, [owner, refresh, toast]);

  const createLink = useCallback(async () => {
    setError(null);
    setCreating(true);
    try {
      // Concept §5: never silently create a link whose key cannot be retained.
      const current = await checkKeyStorage();
      setStorage(current);
      if (!current.available) {
        setError(
          `${current.detail} No link was created, because the key could not be kept in this browser and the conversation would be unreadable.`,
        );
        return;
      }

      const key = await generateConversationKey();
      const created = await invitations.create({ mechanism: current.mechanism });

      // Assembled locally; the server returned a path with no fragment.
      const fragment = await keyToFragmentValue(key);
      const invitationUrl = buildInvitationUrl(location.origin, created.invitationPath, fragment);

      // The URL is kept beside the key, so an owner who closed this dialog can
      // still re-share a link nobody has claimed yet. The server will not hand
      // the token out a second time — it is a bearer credential.
      await storeConversationKey({
        conversationId: created.conversationId,
        key,
        role: 'owner',
        expiresAt: null,
        invitationUrl,
      });

      setNewLink(invitationUrl);

      // Shown in full the first time, so the owner understands what they are
      // about to send — including that the key is in it. After that it stays
      // covered: the link is a bearer credential, and leaving it legible on
      // screen is exactly how it ends up in a screenshot (concept §5).
      const seenBefore = readFlag(SEEN_LINK_FLAG);
      setRevealed(!seenBefore);
      setFirstLink(!seenBefore);
      writeFlag(SEEN_LINK_FLAG);
      await refresh();
    } catch (createError) {
      setError(
        createError instanceof ApiRequestError
          ? createError.message
          : 'The link could not be created.',
      );
    } finally {
      setCreating(false);
    }
  }, [refresh]);

  const selected = useMemo(
    () => conversations?.find((conversation) => conversation.id === selectedId) ?? null,
    [conversations, selectedId],
  );

  if (state === 'loading') {
    return (
      <InterfaceThemeProvider themeId={interfaceTheme} className="msgr">
        <div className="msgr__frame">
          <div className="conv-empty">{t('common.loading')}</div>
        </div>
      </InterfaceThemeProvider>
    );
  }

  if (state === 'signed-out') {
    navigate('/login', { replace: true });
    return null;
  }

  if (state === 'suspended') {
    return (
      <InterfaceThemeProvider themeId={interfaceTheme} className="msgr">
        <div className="msgr__frame" style={{ gridTemplateColumns: '1fr' }}>
          <div className="no-chat">
            <G n="warning" size={54} className="no-chat__art" />
            <h2>{t('messenger.suspendedTitle')}</h2>
            <p>{suspensionReason ?? 'This account has been suspended.'}</p>
            <p>{t('messenger.suspendedBody')}</p>
          </div>
        </div>
      </InterfaceThemeProvider>
    );
  }

  const canOpen = selected?.status === 'active';

  return (
    <InterfaceThemeProvider
      themeId={interfaceTheme}
      accentOverride={theme.accentColour}
      className={selectedId ? 'msgr msgr--chat' : 'msgr'}
    >
      <div className="msgr__frame">
        <ConversationList
          conversations={conversations ?? []}
          loading={conversations === null}
          creating={creating}
          selectedId={selectedId ?? null}
          ownerName={owner?.email ?? ''}
          onSelect={(id) => navigate(`/app/conversations/${id}`)}
          onNewChat={() => void createLink()}
          menu={[
            {
              label: t('messenger.newConversation'),
              glyph: 'new-chat',
              onSelect: () => void createLink(),
            },
            { label: t('common.profile'), glyph: 'user', onSelect: () => navigate('/app/profile') },
            {
              label: t('common.branding'),
              glyph: 'palette',
              onSelect: () => navigate('/app/branding'),
            },
            {
              label: t('common.settings'),
              glyph: 'settings',
              onSelect: () => navigate('/app/settings'),
            },
            {
              label: t('common.signOut'),
              glyph: 'logout',
              danger: true,
              onSelect: () => void auth.signOut().finally(() => navigate('/login')),
            },
          ]}
        />

        {!selectedId ? (
          <div className="no-chat">
            <G n="chat-empty" size={220} className="no-chat__art" />
            <h2>{t('messenger.welcomeTitle')}</h2>
            <p>{t('messenger.welcomeBody', { hours: 24 })}</p>
            <p style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <G n="lock" size={13} /> {t('messenger.encrypted')}
            </p>
          </div>
        ) : !keyChecked ? (
          <div className="msgr__chat">
            <div className="conv-empty">{t('messenger.opening')}</div>
          </div>
        ) : !canOpen ? (
          <div className="msgr__chat">
            <div className="chat-empty" style={{ margin: 'auto' }}>
              {selected?.status === 'pending' ? t('messenger.notOpenedYet') : t('messenger.ended')}
            </div>
          </div>
        ) : (
          <ChatPane
            conversationId={selectedId}
            role="owner"
            encryptionKey={conversationKey}
            title={selected?.name ?? 'Conversation'}
            onBack={() => navigate('/app/conversations')}
            language={language}
            onShowAbout={() => setDialog('about')}
            onReport={() => setDialog('report')}
            extraMenu={[
              {
                glyph: 'trash',
                label: t('chat.endNow'),
                danger: true,
                onSelect: () => setDialog('end'),
              },
            ]}
          />
        )}
      </div>

      {/* ------------------------------------------------------- dialogs */}

      {newLink ? (
        <Modal
          title={t('invite.title')}
          description={t('invite.subtitle')}
          onClose={() => setNewLink(null)}
          footer={
            <>
              <span className="spacer" />
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  // Copying is the whole point of this dialog, so it also
                  // finishes it. One button, one outcome.
                  void navigator.clipboard?.writeText(newLink).finally(() => {
                    toast('success', t('invite.copied'));
                    setNewLink(null);
                  });
                }}
              >
                <G n="copy" size={16} /> {t('common.copy')}
              </button>
            </>
          }
        >
          <button
            type="button"
            className={revealed ? 'secret-link secret-link--revealed' : 'secret-link'}
            onClick={() => setRevealed(true)}
            aria-label={revealed ? t('invite.title') : t('invite.revealLabel')}
          >
            {newLink}
            {!revealed ? <span className="secret-link__hint">{t('invite.reveal')}</span> : null}
          </button>
          <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
            {/*
              In full the first time, so nobody sends their first link without
              reading what it is. After that they are collapsed: the same two
              warnings on every link stop being read and start being dismissed,
              and they crowd out the thing the dialog is actually for.
            */}
            {firstLink ? (
              <>
                <BearerLinkNotice />
                <DeviceLocalKeyNotice />
              </>
            ) : (
              <Disclosure summary={t('invite.whatToKnow')} icon="shield">
                <BearerLinkNotice />
                <DeviceLocalKeyNotice />
              </Disclosure>
            )}
          </div>
        </Modal>
      ) : null}

      {dialog === 'about' ? (
        <Modal title={t('chat.about')} onClose={() => setDialog('none')}>
          <div style={{ display: 'grid', gap: 12 }}>
            <TemporaryNotice hours={24} />
            <EndpointCopyingNotice />
            <EncryptionScopeNotice />
          </div>
        </Modal>
      ) : null}

      {dialog === 'end' && selectedId ? (
        <Modal
          title={t('chat.endTitle')}
          onClose={() => setDialog('none')}
          footer={
            <>
              <span className="spacer" />
              <button type="button" className="btn" onClick={() => setDialog('none')}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={ending}
                onClick={() => {
                  setEnding(true);
                  void invitations
                    .end(selectedId)
                    .then(() => {
                      toast('success', t('chat.ended'));
                      setDialog('none');
                      navigate('/app/conversations');
                      return refresh();
                    })
                    .catch(() => toast('error', t('chat.endFailed')))
                    .finally(() => setEnding(false));
                }}
              >
                {t('chat.endConfirm')}
              </button>
            </>
          }
        >
          <Notice tone="warn" title={t('chat.endTitle')}>
            <p>{t('chat.endBody')}</p>
            <p>{t('chat.endCaveat')}</p>
          </Notice>
        </Modal>
      ) : null}

      {dialog === 'report' && selectedId ? (
        <ReportDialog conversationId={selectedId} onClose={() => setDialog('none')} />
      ) : null}

      {error ? (
        <Modal title={t('invite.failed')} onClose={() => setError(null)}>
          <Notice tone="danger">{error}</Notice>
        </Modal>
      ) : null}

      {storage && !storage.available && !error ? (
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            maxWidth: 460,
            zIndex: 40,
          }}
        >
          <Notice tone="danger" title={t('invite.blockedTitle')}>
            {storage.detail}
          </Notice>
        </div>
      ) : null}
    </InterfaceThemeProvider>
  );
}
